import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { ReminderHistoryRow } from './ReminderItem';
import { getTodayDateStringInTimezone, reminderSkippedOnDate, reminderTasks } from './reminderUtils';
import SnoozeDropdown from './SnoozeDropdown';
import ReminderTaskChecklist from './ReminderTaskChecklist';

const BLAST_COLORS = [
  '#F59E0B', '#FB923C', '#FBBF24', '#38BDF8', '#818CF8',
  '#F472B6', '#34D399', '#FDE68A', '#60A5FA', '#F43F5E',
];

const buildBlastPieces = (count = 48) =>
  Array.from({ length: count }, (_, i) => {
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
    const distance = 120 + Math.random() * 220;
    return {
      id: i,
      color: BLAST_COLORS[i % BLAST_COLORS.length],
      tx: Math.cos(angle) * distance,
      ty: Math.sin(angle) * distance - 40,
      rot: Math.floor(Math.random() * 720 - 360),
      size: 6 + Math.random() * 10,
      delay: Math.random() * 0.25,
      duration: 0.9 + Math.random() * 0.7,
      shape: i % 3,
    };
  });

/**
 * Modal popup shown when a reminder fires (WebSocket) or when viewing a reminder from the list.
 */
const ReminderTriggeredModal = ({
  reminder,
  reminders,
  onClose,
  onComplete,
  onSkip,
  onSnooze,
  onShowAll,
  completing = false,
  skipping = false,
  occurrenceDate,
  onToggleTask,
  togglingTask = false,
}) => {
  const [snoozing, setSnoozing] = useState(false);
  const group = Array.isArray(reminders) && reminders.length > 1 ? reminders : null;
  const blastPieces = useMemo(
    () => buildBlastPieces(48),
    [reminder?.id || reminder?.reminderId || group?.map((item) => item.id).join(',')],
  );

  if (!reminder && !group) return null;

  if (group) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 sm:p-6 announcement-popup-root">
        <div
          className="absolute inset-0 bg-slate-950/55 backdrop-blur-[2px] announcement-popup-backdrop"
          aria-hidden="true"
          onClick={onClose}
        />
        <div
          className="relative w-full max-w-lg announcement-popup-card outline-none"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="reminder-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
            <div className="h-2 w-full bg-gradient-to-r from-amber-400 via-orange-300 to-sky-400" />
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 z-10 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-6 sm:p-8">
              <h3
                id="reminder-modal-title"
                className="text-2xl font-bold text-gray-900 dark:text-white mb-1"
              >
                {group.length} reminders
              </h3>
              <p className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-4">
                Due at the same time
              </p>
              <ul className="mb-6 space-y-2 max-h-64 overflow-y-auto">
                {group.map((item) => (
                  <li
                    key={item.id || item.reminderId || item.title}
                    className="rounded-xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/70 px-4 py-3"
                  >
                    <p className="text-base font-semibold text-gray-900 dark:text-white">
                      {item.title || 'Reminder'}
                    </p>
                    {item.description ? (
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap line-clamp-3">
                        {item.description}
                      </p>
                    ) : null}
                    {reminderTasks(item).length > 0 ? (
                      <p className="mt-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                        {reminderTasks(item).length} tasks
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {onShowAll ? (
                  <button
                    type="button"
                    onClick={onShowAll}
                    className="announcement-ack-btn inline-flex items-center px-6 py-3 text-base font-bold text-white rounded-xl shadow-lg bg-amber-500 hover:bg-amber-600"
                  >
                    Show all
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  const title = reminder.title || 'Reminder';
  const description = reminder.description ? reminder.description.trim() : '';
  const reminderId = reminder.id || reminder._id || reminder.reminderId;
  const todayStr = getTodayDateStringInTimezone(reminder.timezone);
  const completeDate = occurrenceDate || todayStr;
  const alreadyDoneToday =
    Array.isArray(reminder.completedDates) && reminder.completedDates.includes(completeDate);
  const alreadySkippedToday = reminderSkippedOnDate(reminder, completeDate);
  const canSnooze = Boolean(onSnooze && reminderId);
  const canSkip = Boolean(onSkip && reminderId && !alreadyDoneToday && !alreadySkippedToday);

  const handleSnooze = async (minutes) => {
    if (!canSnooze || snoozing) return;
    setSnoozing(true);
    try {
      await onSnooze(minutes, reminder);
    } finally {
      setSnoozing(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 sm:p-6 announcement-popup-root">
      <div
        className="absolute inset-0 bg-slate-950/55 backdrop-blur-[2px] announcement-popup-backdrop"
        aria-hidden="true"
        onClick={onClose}
      />

      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-amber-400/30 via-orange-300/20 to-sky-400/30 announcement-popup-wash"
        aria-hidden="true"
      />

      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {blastPieces.map((piece) => (
          <span
            key={piece.id}
            className={`announcement-blast-piece announcement-blast-shape-${piece.shape}`}
            style={{
              '--tx': `${piece.tx}px`,
              '--ty': `${piece.ty}px`,
              '--rot': `${piece.rot}deg`,
              '--size': `${piece.size}px`,
              '--delay': `${piece.delay}s`,
              '--duration': `${piece.duration}s`,
              backgroundColor: piece.color,
              left: '50%',
              top: '42%',
            }}
          />
        ))}
      </div>

      <div
        className="relative w-full max-w-xl announcement-popup-card outline-none"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="reminder-modal-title"
        aria-describedby="reminder-modal-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
          <div className="h-2 w-full bg-gradient-to-r from-amber-400 via-orange-300 to-sky-400" />
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 z-10 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-200 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="p-6 sm:p-8">
            <div className="flex items-start gap-4 mb-5">
              <div className="announcement-popup-badge flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-300 to-sky-400 flex items-center justify-center shadow-lg">
                <svg className="w-7 h-7 text-white drop-shadow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
                    Reminder
                  </span>
                </div>
                <h3
                  id="reminder-modal-title"
                  className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white leading-tight tracking-tight break-words"
                >
                  {title}
                </h3>
              </div>
            </div>

            <div className="mb-6 max-h-[28rem] overflow-y-auto rounded-xl bg-slate-50 dark:bg-gray-800/70 px-5 py-4 border border-slate-100 dark:border-gray-700">
              <p
                id="reminder-modal-desc"
                className={`whitespace-pre-wrap leading-7 text-base sm:text-lg font-medium ${
                  description
                    ? 'text-gray-800 dark:text-gray-100'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {description || 'No description'}
              </p>
              {reminderTasks(reminder).length > 0 ? (
                <div className="mt-4 pt-3 border-t border-slate-200 dark:border-gray-700">
                  <ReminderTaskChecklist
                    reminder={reminder}
                    dateStr={completeDate}
                    onToggleTask={onToggleTask}
                    disabled={togglingTask || completing || skipping || alreadySkippedToday}
                  />
                </div>
              ) : null}
              {reminder.completedDates || reminder.recurrence ? (
                <div className="mt-4 pt-3 border-t border-slate-200 dark:border-gray-700">
                  <ReminderHistoryRow reminder={reminder} showRangePicker />
                </div>
              ) : null}
            </div>

            {canSnooze ? (
              <div className="mb-5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Snooze
                </p>
                <SnoozeDropdown
                  onSnooze={handleSnooze}
                  disabled={snoozing || completing}
                />
              </div>
            ) : null}

            <div className="flex items-center justify-end gap-2 pt-1 whitespace-nowrap">
              {canSkip && (
                <button
                  type="button"
                  onClick={onSkip}
                  disabled={skipping || completing || snoozing}
                  className="px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-60"
                >
                  {skipping ? 'Skipping…' : alreadySkippedToday ? 'Skipped' : 'Skip for today'}
                </button>
              )}
              {onComplete && (
                <button
                  type="button"
                  onClick={onComplete}
                  disabled={completing || alreadyDoneToday || snoozing || skipping}
                  className="announcement-ack-btn inline-flex items-center px-4 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {completing ? 'Completing…' : alreadyDoneToday ? 'Completed' : 'Mark complete'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReminderTriggeredModal;
